import { useDispatch } from "react-redux";
import { useEffect, useRef } from "react";
import { io, Socket } from "socket.io-client";
import { useLocation } from "react-router-dom";
import { getAuthToken } from "@dynamic-labs/sdk-react-core";

import { Env, Paths } from "../../common/constants";
import { getBatchListing } from "../../pages/batches/services/action";
import { setCoaTokenizationForBatch } from "../../pages/batches/services/slices";

// Shared socket instance persisted across component mounts
let sharedSocket: Socket | null = null;

const SocketImplementation = () => {
  const location = useLocation();
  const dispatch = useDispatch();
  const pathname = location.pathname;

  const socketRef = useRef<Socket | null>(null);

  useEffect(() => {
    const shouldConnect =
      pathname.includes(Paths.BATCHES) || pathname.includes(Paths.ADD_STRAIN);

    if (!shouldConnect) {
      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
        return;
      }
    } else {
      const accessToken = getAuthToken();

      // Reuse existing shared socket if available
      if (sharedSocket) {
        // Ensure latest token in query before (re)connect
        try {
          (sharedSocket.io as any).opts = {
            ...(sharedSocket.io as any).opts,
            query: { accessToken },
          };
        } catch {}

        if (!sharedSocket.connected) {
          sharedSocket.connect();
        }
        socketRef.current = sharedSocket;
      } else {
        const socket = io(Env.SOCKET_URL, {
          transports: ["websocket"],
          autoConnect: true,
          query: { accessToken },
          reconnection: true,
          reconnectionAttempts: 5,
        });
        sharedSocket = socket;
        socketRef.current = socket;
      }

      const socket = socketRef.current!;

      // Bind listeners only once
      if (!(socket as any)._listenersBound) {
        socket.on("connect", () => {
          console.log("Socket connected:", socket.id);
        });

        socket.on("disconnect", (reason) => {
          console.log("Socket disconnected:", reason);
        });

        socket.on("coa-status-update", (data) => {
          console.log("COA Status Updated:", data);
          try {
            if (data && typeof data === "object") {
              // Case 1: tokenization update payload
              const batchMongoId = (data as any).batchMongoId;
              const isCoaTokenized = (data as any).isCoaTokenized;
              const coaTokenizedAt = (data as any).coaTokenizedAt;
              if (batchMongoId && typeof isCoaTokenized === "boolean") {
                dispatch(
                  setCoaTokenizationForBatch({
                    batchMongoId,
                    isCoaTokenized,
                    coaTokenizedAt,
                  }),
                );
                return;
              }
            }
          } catch (error) {
            // Log malformed payloads for debugging but don't crash the app
            console.error(
              "Failed to process COA status update:",
              error,
              "Data received:",
              data,
            );
          }
        });

        socket.on("product-tokenization-status", (data) => {
          console.log("Product Tokenization Update:", data);
          try {
            if (data && typeof data === "object") {
              const isProductTokenized = (data as any).isProductTokenized;
              if (isProductTokenized) {
                dispatch(getBatchListing("non_tokenized") as any);
                return;
              }
            }
          } catch (error) {
            // Log malformed payloads for debugging but don't crash the app
            console.error(
              "Failed to process product tokenization update:",
              error,
              "Data received:",
              data,
            );
          }
        });

        // Reconnect listeners
        socket.on("reconnect_attempt", () => console.log("Reconnecting..."));
        socket.on("reconnect_error", (err) =>
          console.log("Reconnect error:", err),
        );
        socket.on("reconnect", (attempt) =>
          console.log("Reconnected on attempt", attempt),
        );

        (socket as any)._listenersBound = true;
      }
    }

    // eslint-disable-next-line
  }, [pathname]);

  return null;
};

export default SocketImplementation;
